// Implements: F-GEN-033
/**
 * Which legend keys one stored diagram really uses (owner 2026-10-19, answer 6A). Pure.
 *
 * `diagram/v2` marks the start screen with the class `start` (`:::start` or `class a start`, drawn
 * by its `classDef start …` as a coloured border) and the alternative paths with dotted edges
 * (`-.->`, `-.-`, `-. texto .->`, `-.texto.->`). A journey drawn by `diagram/v1`, or a model that left a mark
 * out, simply has fewer keys: "Pantalla" and "Flecha" always show, and a missing mark is never an
 * error (checklist 11).
 *
 * Text is not syntax: comments (`%%`), quoted strings, `|edge labels|` and bracketed node labels
 * are blanked before looking, so a label that reads "Paso 1-.2" or ":::start" marks nothing.
 */

export interface LegendKeys {
  /** The diagram gives one node the class `start` AND defines it, so a border is drawn. */
  readonly start: boolean;
  /** The diagram has at least one dotted edge. */
  readonly alternative: boolean;
}

export const NO_MARKS: LegendKeys = { start: false, alternative: false };

const COMMENT = /^[ \t]*%%.*$/gm;
const QUOTED = /"[^"\n]*"/g;
const BARRED = /\|[^|\n]*\|/g;
const BRACKETED = /\[[^\]\n]*\]|\([^)\n]*\)|\{[^}\n]*\}/g;

const START_DEFINED = /^[ \t]*classDef[ \t]+start[ \t]/m;
const START_SHORTHAND = /:::start(?![\w-])/;
// eslint-disable-next-line security/detect-unsafe-regex -- linear: each repeated `, name` must start at a comma `[\w-]` cannot match, and no two `[ \t]*` runs touch (the timing test in legendKeys.test.ts holds it).
const START_STATEMENT = /^[ \t]*class[ \t]+[\w-]+(?:[ \t]*,[ \t]*[\w-]+)*[ \t]+start[ \t]*(?:;[ \t]*)?$/m;

/**
 * `-.-`, `-.->`, `-..->`, `<-.->`, and the inline-text form with or without its spaces
 * (`-. texto .->`, `-.texto.->`). Spaceless, the text is one line with no `>`, opened right after
 * `-.` by neither a space, a dot, a dash nor a head, so `-->`, `==>` and `-- texto -->` never match.
 */
const DOTTED_EDGE = /-\.+-|-\.[ \t]|[ \t]\.+->|-\.[^\s.>-][^\n>]*?\.+->/;

function syntaxOnly(source: string): string {
  return source.replace(COMMENT, "").replace(QUOTED, '""').replace(BARRED, "||").replace(BRACKETED, "[]");
}

export function legendKeysOf(source: string): LegendKeys {
  const syntax = syntaxOnly(source);
  const start = START_DEFINED.test(syntax) && (START_SHORTHAND.test(syntax) || START_STATEMENT.test(syntax));
  return { start, alternative: DOTTED_EDGE.test(syntax) };
}
